/**
 * Board — THE canonical kanban primitive (UI/UX Patterns PRD Issue 11).
 *
 * One board, configured by data + adapters, replacing the fleet of forked
 * boards (CRM PipelineSurface/PipelineBoard, Hiring PipelineKanban, Work
 * KanbanBoard, StreamStageBoard/KindBoard). The engine is generalized from
 * `PipelineSurface` — the strongest of the forks: real dnd-kit drag, optimistic
 * placement with revert-on-failed-persist, horizontal column-scroll with a
 * dynamic edge-fade, and sticky one-line column headers.
 *
 * Everything domain-specific rides on adapters, never baked in:
 *   • `columns`      — the state-machine columns + the items currently in each.
 *   • `getItemId`    — a stable id per item (drag identity + placement key).
 *   • `renderCard`   — the caller draws the card body; Board wraps it in the
 *                      draggable/clickable tile shell.
 *   • `onMove`       — generic `(itemId, toColumnId, pos)`; Board owns the
 *                      optimism and hands back the drop neighbours.
 *   • `summarySlot` / `headerSlot` — a KPI band / title row above the columns.
 *   • per-column `badge`, `intent`, `droppable`, `emptyLabel`.
 *
 * Ordering. Board tracks an *ordered* optimistic placement (itemKey[] per
 * column), so a move re-columns AND re-positions in place. Column-to-column
 * moves work out of the box; pass `sortable` to also make cards drop targets so
 * a drop *onto a card* re-orders within a column (the drop neighbours arrive in
 * `pos.beforeId`/`pos.afterId`, e.g. for a `sort_key` write). Without
 * `sortable`, only columns are drop targets (append) — the CRM behaviour.
 *
 * Read-only mode: omit `onMove` (or pass `readOnly`) and the board renders the
 * same columns with no DnD — the faithful replacement for KindBoard's
 * presentation grid, minus the fork. In read-only mode NO dnd-kit hook runs, so
 * the board is safe to render without a `DndContext` ancestor.
 *
 * Pure presentation: the block never fetches, never sees a raw row, and never
 * formats a value — `renderCard` receives already-shaped items and Board only
 * moves ids around (display-consistency rail).
 */

import * as React from "react";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  closestCorners,
  useDraggable,
  useDroppable,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import { type Intent } from "../tokens/intents";
import { IntentDot } from "./status-dot";
import { cn } from "../utils";

export type BoardColumnId = string | number;

/** The drop neighbours around an item's new position (for a `sort_key` write). */
export interface BoardMovePosition {
  /** The item immediately before the dropped item, or null at the column head. */
  beforeId: BoardColumnId | null;
  /** The item immediately after the dropped item, or null at the column tail. */
  afterId: BoardColumnId | null;
}

/** One state-machine column of the board. */
export interface BoardColumn<TItem> {
  id: BoardColumnId;
  /** Header label. */
  name: string;
  /** Items currently resting in this column (their "home"). */
  items: TItem[];
  /** Colors the sticky header dot (e.g. via a domain `*Intent` helper). */
  intent?: Intent;
  /** Right-of-name slot — count, money sum, WIP badge. Pre-formatted. */
  badge?: React.ReactNode;
  /** A terminal / read-only column rejects drops when false. Default true. */
  droppable?: boolean;
  /** Shown in an empty droppable column. Default "Drop here". */
  emptyLabel?: React.ReactNode;
}

export interface BoardProps<TItem> {
  columns: BoardColumn<TItem>[];
  /** Stable id per item — drag identity + optimistic-placement key. */
  getItemId: (item: TItem) => BoardColumnId;
  /** Draw the card body; Board owns the draggable/clickable shell. */
  renderCard: (item: TItem, ctx: { dragging: boolean }) => React.ReactNode;
  /**
   * Persist a move. Board applies it optimistically and reverts if the promise
   * rejects. `pos` carries the drop neighbours (for a `sort_key` write); a
   * column-move caller can ignore it. Omit `onMove` for a read-only board.
   */
  onMove?: (
    itemId: BoardColumnId,
    toColumnId: BoardColumnId,
    pos: BoardMovePosition,
  ) => void | Promise<void>;
  /**
   * Make cards drop targets so a drop onto a card re-orders within a column.
   * Off (default) = only columns are drop targets (append), the CRM behaviour.
   */
  sortable?: boolean;
  /**
   * The card wrapper omits the default tile chrome (bg/padding/hover) so
   * `renderCard` can supply a complete card (e.g. `TaskCard`). Default false —
   * Board draws a surface-shaded tile around the card body.
   */
  bareCards?: boolean;
  /** Click a card. */
  onOpen?: (item: TItem) => void;
  /** Force read-only even if `onMove` is provided. */
  readOnly?: boolean;
  /** A title row (search / create) rendered above the summary + columns. */
  headerSlot?: React.ReactNode;
  /** A KPI / summary band rendered between the header and the columns. */
  summarySlot?: React.ReactNode;
  /** Column width in px. Default 272. */
  columnWidth?: number;
  /** Unique DnD context id when multiple boards mount on one page. */
  dndId?: string;
  className?: string;
}

const DRAG_PREFIX = "board-item:";
const DROP_PREFIX = "board-col:";

const parseDrag = (id: unknown): string | null => {
  const s = String(id);
  return s.startsWith(DRAG_PREFIX) ? s.slice(DRAG_PREFIX.length) : null;
};
const parseDrop = (id: unknown): string | null => {
  if (id == null) return null;
  const s = String(id);
  return s.startsWith(DROP_PREFIX) ? s.slice(DROP_PREFIX.length) : null;
};

/** Which card is mid-drag (by item key), so the resting tile dims. */
const BoardActiveContext = React.createContext<string | null>(null);

/** One item flattened out of its column, carrying its stable keys + home. */
interface FlatItem<TItem> {
  item: TItem;
  /** The caller's original (typed) item id — passed back through `onMove`. */
  id: BoardColumnId;
  /** String form of the item id — drag identity + placement key. */
  key: string;
  /** String form of the item's home column id. */
  home: string;
}

type Order = Record<string, string[]>;

function buildOrder<TItem>(
  columns: BoardColumn<TItem>[],
  getItemId: (item: TItem) => BoardColumnId,
): Order {
  const o: Order = {};
  for (const col of columns) {
    o[String(col.id)] = col.items.map((i) => String(getItemId(i)));
  }
  return o;
}

const sameSeq = (a: string[] | undefined, b: string[] | undefined) =>
  (a ?? []).length === (b ?? []).length &&
  (a ?? []).every((v, i) => v === (b ?? [])[i]);

export function Board<TItem>({
  columns,
  getItemId,
  renderCard,
  onMove,
  sortable = false,
  bareCards = false,
  onOpen,
  readOnly,
  headerSlot,
  summarySlot,
  columnWidth = 272,
  dndId = "board-dnd",
  className,
}: BoardProps<TItem>) {
  const interactive = !readOnly && typeof onMove === "function";

  // String key → original (typed) column id, so a move reported by drop-id
  // string can persist the caller's real column id (number OR string).
  const colIdByKey = React.useMemo(
    () => new Map(columns.map((c) => [String(c.id), c.id] as const)),
    [columns],
  );

  // Flatten once, preserving column + within-column order, with each item's home
  // column + a key→item lookup.
  const { flat, byKey } = React.useMemo(() => {
    const out: FlatItem<TItem>[] = [];
    const map = new Map<string, FlatItem<TItem>>();
    for (const col of columns) {
      const home = String(col.id);
      for (const item of col.items) {
        const id = getItemId(item);
        const f: FlatItem<TItem> = { item, id, key: String(id), home };
        out.push(f);
        map.set(f.key, f);
      }
    }
    return { flat: out, byKey: map };
  }, [columns, getItemId]);

  // Ordered optimistic placement: columnKey → itemKey[]. Reconciles when the
  // server set changes (new items, external moves, persisted reorder).
  const fingerprint = flat.map((f) => `${f.key}@${f.home}`).join(",");
  const [order, setOrder] = React.useState<Order>(() => buildOrder(columns, getItemId));
  React.useEffect(() => {
    setOrder(buildOrder(columns, getItemId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fingerprint]);

  const columnKeyOfItem = React.useCallback(
    (itemKey: string) => {
      for (const colKey of Object.keys(order)) {
        if (order[colKey]!.includes(itemKey)) return colKey;
      }
      return byKey.get(itemKey)?.home ?? null;
    },
    [order, byKey],
  );

  const itemsInColumn = React.useCallback(
    (colKey: string): FlatItem<TItem>[] =>
      (order[colKey] ?? [])
        .map((k) => byKey.get(k))
        .filter((f): f is FlatItem<TItem> => f != null),
    [order, byKey],
  );

  const applyDrop = React.useCallback(
    async (activeKey: string, overId: unknown) => {
      if (!onMove) return;
      const activeCol = columnKeyOfItem(activeKey);
      if (!activeCol) return;

      const overColKey = parseDrop(overId);
      const overItemKey = parseDrag(overId);
      let targetCol: string;
      if (overColKey != null) targetCol = overColKey;
      else if (overItemKey != null) {
        const c = columnKeyOfItem(overItemKey);
        if (!c) return;
        targetCol = c;
      } else return;

      const targetColumn = columns.find((c) => String(c.id) === targetCol);
      if (!targetColumn || targetColumn.droppable === false) return;

      // Without card drop targets there is no within-column re-order.
      if (!sortable && activeCol === targetCol) return;

      const prev = order;
      const source = (prev[activeCol] ?? []).filter((k) => k !== activeKey);
      const target =
        activeCol === targetCol
          ? source
          : (prev[targetCol] ?? []).filter((k) => k !== activeKey);
      const index =
        overItemKey && overItemKey !== activeKey
          ? (() => {
              const i = target.indexOf(overItemKey);
              return i === -1 ? target.length : i;
            })()
          : target.length;
      target.splice(index, 0, activeKey);

      const next: Order = { ...prev, [activeCol]: source, [targetCol]: target };
      if (activeCol === targetCol) next[targetCol] = target;

      if (
        activeCol === targetCol &&
        sameSeq(prev[targetCol], next[targetCol])
      ) {
        return; // dropped in place — no-op
      }

      const posIdx = target.indexOf(activeKey);
      const beforeKey = posIdx > 0 ? target[posIdx - 1]! : null;
      const afterKey = posIdx < target.length - 1 ? target[posIdx + 1]! : null;
      const pos: BoardMovePosition = {
        beforeId: beforeKey ? byKey.get(beforeKey)?.id ?? null : null,
        afterId: afterKey ? byKey.get(afterKey)?.id ?? null : null,
      };

      const activeId = byKey.get(activeKey)?.id;
      const targetColId = colIdByKey.get(targetCol);
      if (activeId == null || targetColId == null) return;

      setOrder(next);
      try {
        await onMove(activeId, targetColId, pos);
      } catch {
        setOrder(prev);
      }
    },
    [onMove, sortable, order, columns, columnKeyOfItem, byKey, colIdByKey],
  );

  const body = (
    <BoardScroller
      columns={columns}
      columnWidth={columnWidth}
      itemsInColumn={itemsInColumn}
      renderCard={renderCard}
      onOpen={onOpen}
      interactive={interactive}
      sortable={sortable}
      bareCards={bareCards}
    />
  );

  return (
    <div className={cn("flex h-full min-h-0 flex-col", className)}>
      {headerSlot}
      {summarySlot}
      {interactive ? (
        <BoardDnd dndId={dndId} flat={flat} onDrop={applyDrop}>
          {(active) => (
            <>
              {body}
              <DragOverlay dropAnimation={null}>
                {active ? (
                  <div
                    className={cn(
                      "rotate-1 cursor-grabbing rounded-lg shadow-lg",
                      bareCards
                        ? ""
                        : "bg-surface-4 px-3 py-2.5 ring-1 ring-border",
                    )}
                    style={{ width: columnWidth - 16 }}
                  >
                    {renderCard(active.item, { dragging: true })}
                  </div>
                ) : null}
              </DragOverlay>
            </>
          )}
        </BoardDnd>
      ) : (
        body
      )}
    </div>
  );
}

// ─── DnD wrapper ─────────────────────────────────────────────────────────────

function BoardDnd<TItem>({
  dndId,
  flat,
  onDrop,
  children,
}: {
  dndId: string;
  flat: FlatItem<TItem>[];
  onDrop: (activeKey: string, overId: unknown) => void;
  children: (active: FlatItem<TItem> | null) => React.ReactNode;
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );
  const [activeKey, setActiveKey] = React.useState<string | null>(null);
  const active = React.useMemo(
    () => flat.find((f) => f.key === activeKey) ?? null,
    [flat, activeKey],
  );

  function handleEnd(e: DragEndEvent) {
    setActiveKey(null);
    const itemKey = parseDrag(e.active.id);
    if (itemKey == null || e.over?.id == null) return;
    onDrop(itemKey, e.over.id);
  }

  return (
    <DndContext
      id={dndId}
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={(e: DragStartEvent) => setActiveKey(parseDrag(e.active.id))}
      onDragEnd={handleEnd}
      onDragCancel={() => setActiveKey(null)}
    >
      <BoardActiveContext.Provider value={active?.key ?? null}>
        {children(active)}
      </BoardActiveContext.Provider>
    </DndContext>
  );
}

// ─── Scroller (columns + edge-fade) ──────────────────────────────────────────

function BoardScroller<TItem>({
  columns,
  columnWidth,
  itemsInColumn,
  renderCard,
  onOpen,
  interactive,
  sortable,
  bareCards,
}: {
  columns: BoardColumn<TItem>[];
  columnWidth: number;
  itemsInColumn: (colKey: string) => FlatItem<TItem>[];
  renderCard: (item: TItem, ctx: { dragging: boolean }) => React.ReactNode;
  onOpen?: (item: TItem) => void;
  interactive: boolean;
  sortable: boolean;
  bareCards: boolean;
}) {
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const [edges, setEdges] = React.useState({ left: false, right: false });
  const refreshEdges = React.useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setEdges({
      left: el.scrollLeft > 4,
      right: el.scrollLeft + el.clientWidth < el.scrollWidth - 4,
    });
  }, []);
  React.useEffect(() => {
    refreshEdges();
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(refreshEdges);
    ro.observe(el);
    return () => ro.disconnect();
  }, [refreshEdges]);

  return (
    <div className="relative min-h-0 flex-1">
      <div
        ref={scrollRef}
        onScroll={refreshEdges}
        className="flex h-full gap-5 overflow-x-auto p-4"
      >
        {columns.map((col) => {
          const colKey = String(col.id);
          const items = itemsInColumn(colKey);
          const droppable = interactive && col.droppable !== false;
          const header = <ColumnHeader column={col} count={items.length} />;
          const list = items.map((f) =>
            interactive ? (
              <DraggableCard
                key={f.key}
                flat={f}
                renderCard={renderCard}
                onOpen={onOpen}
                sortable={sortable && col.droppable !== false}
                bare={bareCards}
              />
            ) : (
              <StaticCard
                key={f.key}
                flat={f}
                renderCard={renderCard}
                onOpen={onOpen}
                bare={bareCards}
              />
            ),
          );
          return droppable ? (
            <DroppableColumn
              key={colKey}
              colKey={colKey}
              width={columnWidth}
              header={header}
              empty={col.emptyLabel ?? "Drop here"}
              isEmpty={items.length === 0}
            >
              {list}
            </DroppableColumn>
          ) : (
            <ColumnShell
              key={colKey}
              width={columnWidth}
              header={header}
              empty={col.emptyLabel ?? "Nothing here yet."}
              isEmpty={items.length === 0}
            >
              {list}
            </ColumnShell>
          );
        })}
      </div>
      <EdgeFade side="left" show={edges.left} />
      <EdgeFade side="right" show={edges.right} />
    </div>
  );
}

function EdgeFade({ side, show }: { side: "left" | "right"; show: boolean }) {
  return (
    <div
      aria-hidden
      className={cn(
        "pointer-events-none absolute inset-y-0 w-12 transition-opacity duration-200",
        side === "left"
          ? "left-0 bg-gradient-to-r from-card to-transparent"
          : "right-0 bg-gradient-to-l from-card to-transparent",
        show ? "opacity-100" : "opacity-0",
      )}
    />
  );
}

// ─── Column ──────────────────────────────────────────────────────────────────

function ColumnHeader<TItem>({
  column,
  count,
}: {
  column: BoardColumn<TItem>;
  count: number;
}) {
  return (
    <div className="sticky top-0 z-10 flex items-center justify-between gap-2 bg-card/95 px-1 pb-2.5 backdrop-blur">
      <span className="flex min-w-0 items-center gap-2">
        {column.intent ? <IntentDot intent={column.intent} size="sm" /> : null}
        <span className="truncate type-body font-medium text-foreground">
          {column.name}
        </span>
        <span className="type-subtext tabular-nums text-muted-foreground">{count}</span>
      </span>
      {column.badge != null ? (
        <span className="shrink-0 type-subtext tabular-nums text-muted-foreground">
          {column.badge}
        </span>
      ) : null}
    </div>
  );
}

/** Pure column markup — no dnd-kit hook. Used directly for read-only / terminal columns. */
function ColumnShell({
  width,
  header,
  children,
  empty,
  isEmpty,
  isOver,
  bodyRef,
}: {
  width: number;
  header: React.ReactNode;
  children: React.ReactNode;
  empty: React.ReactNode;
  isEmpty: boolean;
  isOver?: boolean;
  bodyRef?: React.Ref<HTMLDivElement>;
}) {
  return (
    <section className="group/col flex h-full min-h-0 shrink-0 flex-col" style={{ width }}>
      {header}
      <div
        ref={bodyRef}
        className={cn(
          "flex min-h-0 flex-1 flex-col gap-1.5 overflow-y-auto rounded-lg p-1 transition-colors",
          isOver && "bg-surface-2/70",
        )}
      >
        {children}
        {isEmpty ? (
          <div
            className={cn(
              "rounded-lg px-3 py-6 text-center type-subtext transition-colors",
              isOver
                ? "bg-surface-3/70 text-foreground"
                : "bg-surface-3/30 text-muted-foreground",
            )}
          >
            {empty}
          </div>
        ) : null}
      </div>
    </section>
  );
}

/** Droppable column — the dnd-kit hook lives here, so it only runs inside a DndContext. */
function DroppableColumn({
  colKey,
  width,
  header,
  children,
  empty,
  isEmpty,
}: {
  colKey: string;
  width: number;
  header: React.ReactNode;
  children: React.ReactNode;
  empty: React.ReactNode;
  isEmpty: boolean;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: `${DROP_PREFIX}${colKey}` });
  return (
    <ColumnShell
      width={width}
      header={header}
      empty={empty}
      isEmpty={isEmpty}
      isOver={isOver}
      bodyRef={setNodeRef}
    >
      {children}
    </ColumnShell>
  );
}

// ─── Card ────────────────────────────────────────────────────────────────────

function cardClass(bare: boolean, extra: string) {
  return cn(
    bare
      ? "block w-full rounded-lg text-left"
      : "block w-full rounded-lg bg-surface-3 px-3 py-2.5 text-left transition-colors hover:bg-surface-4",
    extra,
  );
}

/** Static card — no dnd-kit hook. Used in read-only mode. */
function StaticCard<TItem>({
  flat,
  renderCard,
  onOpen,
  bare,
}: {
  flat: FlatItem<TItem>;
  renderCard: (item: TItem, ctx: { dragging: boolean }) => React.ReactNode;
  onOpen?: (item: TItem) => void;
  bare: boolean;
}) {
  const clickable = typeof onOpen === "function";
  return (
    <div
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={clickable ? () => onOpen?.(flat.item) : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onOpen?.(flat.item);
              }
            }
          : undefined
      }
      className={cardClass(bare, clickable ? "cursor-pointer" : "cursor-default")}
    >
      {renderCard(flat.item, { dragging: false })}
    </div>
  );
}

/**
 * Draggable card — the dnd-kit hooks live here, so they only run inside a
 * DndContext. When `sortable`, the card is ALSO a drop target (same id) so a
 * drop onto it re-orders within the column.
 */
function DraggableCard<TItem>({
  flat,
  renderCard,
  onOpen,
  sortable,
  bare,
}: {
  flat: FlatItem<TItem>;
  renderCard: (item: TItem, ctx: { dragging: boolean }) => React.ReactNode;
  onOpen?: (item: TItem) => void;
  sortable: boolean;
  bare: boolean;
}) {
  const activeKey = React.useContext(BoardActiveContext);
  const dimmed = activeKey === flat.key;
  const { setNodeRef, attributes, listeners, isDragging } = useDraggable({
    id: `${DRAG_PREFIX}${flat.key}`,
  });
  const drop = useDroppable({ id: `${DRAG_PREFIX}${flat.key}`, disabled: !sortable });
  const setRefs = React.useCallback(
    (el: HTMLElement | null) => {
      setNodeRef(el);
      drop.setNodeRef(el);
    },
    [setNodeRef, drop],
  );
  return (
    <div
      ref={setRefs}
      onClick={onOpen ? () => onOpen(flat.item) : undefined}
      {...listeners}
      {...attributes}
      className={cardClass(
        bare,
        cn(
          "cursor-grab touch-none active:cursor-grabbing",
          (dimmed || isDragging) && "opacity-40",
          sortable && drop.isOver && !dimmed && "ring-1 ring-foreground/40",
        ),
      )}
    >
      {renderCard(flat.item, { dragging: false })}
    </div>
  );
}
